<template>
    <div class="gs-checkout-rule-editor">
        <section class="gs-checkout-default-card">
            <div class="gs-checkout-default-card__copy">
                <small>Default quantity setting</small>
                <h4>Set the normal quantity rule for eligible price points.</h4>
                <p>One-time paid price points can inherit this rule, then any special offer can be modified separately.</p>
            </div>
            <div class="gs-checkout-default-card__body">
                <div class="gs-checkout-choice-row">
                    <button
                        v-for="quantityType in quantityTypes"
                        :key="quantityType.id"
                        type="button"
                        class="gs-checkout-choice"
                        :class="{ 'is-selected': defaultQuantity.enabled == quantityType.id }"
                        @click="setDefaultQuantityEnabled(quantityType.id)"
                    >
                        <span></span>
                        <strong>{{ quantityType.title }}</strong>
                        <em>{{ quantityType.description }}</em>
                    </button>
                </div>
                <div v-if="defaultQuantity.enabled == 1" class="gs-checkout-inline-fields">
                    <label>
                        <span>{{ $t('frontend_product_step5_min_value') }}</span>
                        <gd-dropdown v-model="defaultQuantity.minimum" size="full" :options="quantityOptions" @change="syncDefaultQuantityToInherited" />
                    </label>
                    <label>
                        <span>{{ $t('frontend_product_step5_max_value') }}</span>
                        <gd-dropdown v-model="defaultQuantity.maximum" size="full" :options="quantityOptions" @change="syncDefaultQuantityToInherited" />
                    </label>
                </div>
            </div>
        </section>

        <section class="gs-checkout-price-rules">
            <div class="gs-checkout-price-rules__head">
                <div>
                    <h4>Price point quantity settings</h4>
                    <p>Eligible price points can inherit the default quantity rule or use a custom override.</p>
                </div>
            </div>

            <article
                v-for="(pricePoint, index) in localModel.pricing_points"
                :key="pricePoint.id || index"
                class="gs-checkout-price-rule"
                :class="{ 'is-custom': !isUsingDefaultQuantity(pricePoint), 'is-disabled': !isQuantityEligible(pricePoint) }"
            >
                <header class="gs-checkout-price-rule__head">
                    <div>
                        <strong>{{ pricePoint.name }}</strong>
                        <span>{{ quantitySummary(pricePoint) }}</span>
                    </div>
                    <div class="gs-checkout-price-rule__actions" v-if="isQuantityEligible(pricePoint)">
                        <em :class="{ 'is-custom': !isUsingDefaultQuantity(pricePoint) }">
                            {{ isUsingDefaultQuantity(pricePoint) ? 'Default' : 'Custom' }}
                        </em>
                        <label>
                            <span>Apply default</span>
                            <gd-toggle
                                aria-label="Apply the default quantity settings"
                                :value="isUsingDefaultQuantity(pricePoint)"
                                @input="toggleQuantityDefault(pricePoint, $event)"
                            />
                        </label>
                        <button type="button" @click="toggleQuantityEditor(index)">
                            {{ openQuantityIndex === index ? 'Collapse' : 'Edit settings' }}
                        </button>
                    </div>
                </header>

                <div v-if="openQuantityIndex === index && !isUsingDefaultQuantity(pricePoint)" class="gs-checkout-price-rule__body">
                    <div class="gs-checkout-choice-row">
                        <button
                            v-for="quantityType in quantityTypes"
                            :key="quantityType.id"
                            type="button"
                            class="gs-checkout-choice"
                            :class="{ 'is-selected': pricePoint.quantity_enable == quantityType.id }"
                            @click="pricePoint.quantity_enable = quantityType.id"
                        >
                            <span></span>
                            <strong>{{ quantityType.title }}</strong>
                            <em>{{ quantityType.description }}</em>
                        </button>
                    </div>
                    <div v-if="pricePoint.quantity_enable == 1" class="gs-checkout-inline-fields">
                        <label>
                            <span>{{ $t('frontend_product_step5_min_value') }}</span>
                            <gd-dropdown v-model="pricePoint.minimum_quantity" size="full" :options="quantityOptions" />
                        </label>
                        <label>
                            <span>{{ $t('frontend_product_step5_max_value') }}</span>
                            <gd-dropdown v-model="pricePoint.maximum_quantity" size="full" :options="quantityOptions" />
                        </label>
                    </div>
                </div>
            </article>
        </section>
    </div>
</template>

<script>
export default {
    props: {
        model: {
            required: true,
        },
        validation: {
            required: true,
        },
    },
    data() {
        const firstEligible = (this.model.pricing_points || []).find((pricePoint) => {
            return this.isQuantityEligible(pricePoint);
        });

        return {
            localModel: this.model,
            openQuantityIndex: null,
            defaultQuantity: {
                enabled: firstEligible ? firstEligible.quantity_enable || 0 : 0,
                minimum: firstEligible ? firstEligible.minimum_quantity || 1 : 1,
                maximum: firstEligible ? firstEligible.maximum_quantity || 1 : 1,
            },
            quantityTypes: [
                {
                    id: 0,
                    title: this.$t('frontend_product_step5_quantity_types0_title'),
                    description: this.$t('frontend_product_step5_quantity_types0_desc'),
                },
                {
                    id: 1,
                    title: this.$t('frontend_product_step5_quantity_types1_title'),
                    description: this.$t('frontend_product_step5_quantity_types1_desc'),
                },
            ],
        }
    },
    mounted() {
        this.ensureQuantityDefaults();
    },
    watch: {
        localModel: {
            handler() {
                this.$emit('update-model', this.localModel);
                this.$emit('updateModel', this.localModel);
            },
            deep: true,
        },
    },
    computed: {
        quantityOptions() {
            const quantityOptions = [];
            for (let i = 1; i <= 100; i++) {
                quantityOptions.push({
                    value: i,
                    text: i,
                });
            }
            return quantityOptions;
        },
    },
    methods: {
        isQuantityEligible(pricePoint) {
            return pricePoint.type == 1 && pricePoint.is_trial == 0 && pricePoint.price > 0;
        },
        ensureQuantityDefaults() {
            if (!this.localModel.checkout_page_options) {
                this.$set(this.localModel, 'checkout_page_options', {});
            }
            if (!this.localModel.checkout_page_options.quantity_default_options) {
                this.$set(this.localModel.checkout_page_options, 'quantity_default_options', { ...this.defaultQuantity });
            }
            this.localModel.pricing_points.forEach((pricePoint) => {
                if (pricePoint.quantity_use_default == null) {
                    this.$set(pricePoint, 'quantity_use_default', this.quantityMatchesDefault(pricePoint));
                }
                if (pricePoint.quantity_use_default && this.isQuantityEligible(pricePoint)) {
                    this.applyDefaultQuantity(pricePoint);
                }
            });
        },
        quantityMatchesDefault(pricePoint) {
            return String(pricePoint.quantity_enable || 0) === String(this.defaultQuantity.enabled) &&
                String(pricePoint.minimum_quantity || 1) === String(this.defaultQuantity.minimum) &&
                String(pricePoint.maximum_quantity || 1) === String(this.defaultQuantity.maximum);
        },
        isUsingDefaultQuantity(pricePoint) {
            return pricePoint.quantity_use_default !== false;
        },
        applyDefaultQuantity(pricePoint) {
            this.$set(pricePoint, 'quantity_use_default', true);
            this.$set(pricePoint, 'quantity_enable', this.defaultQuantity.enabled);
            this.$set(pricePoint, 'minimum_quantity', this.defaultQuantity.minimum);
            this.$set(pricePoint, 'maximum_quantity', this.defaultQuantity.maximum);
        },
        syncDefaultQuantityToInherited() {
            this.$set(this.localModel.checkout_page_options, 'quantity_default_options', { ...this.defaultQuantity });
            this.localModel.pricing_points.forEach((pricePoint) => {
                if (this.isQuantityEligible(pricePoint) && this.isUsingDefaultQuantity(pricePoint)) {
                    this.applyDefaultQuantity(pricePoint);
                }
            });
        },
        setDefaultQuantityEnabled(value) {
            this.defaultQuantity.enabled = value;
            this.syncDefaultQuantityToInherited();
        },
        toggleQuantityDefault(pricePoint, value) {
            this.$set(pricePoint, 'quantity_use_default', !!value);
            if (value) {
                this.applyDefaultQuantity(pricePoint);
            }
        },
        toggleQuantityEditor(index) {
            const pricePoint = this.localModel.pricing_points[index];
            if (!this.isQuantityEligible(pricePoint)) return;
            if (this.isUsingDefaultQuantity(pricePoint)) {
                this.toggleQuantityDefault(pricePoint, false);
            }
            this.openQuantityIndex = this.openQuantityIndex === index ? null : index;
        },
        quantitySummary(pricePoint) {
            if (!this.isQuantityEligible(pricePoint)) return this.$t('frontend_product_step5_multiple_quantities_not_available');
            if (this.isUsingDefaultQuantity(pricePoint)) return 'Uses the default quantity setting';
            if (pricePoint.quantity_enable == 1) return `Custom quantity range: ${pricePoint.minimum_quantity || 1} to ${pricePoint.maximum_quantity || 1}`;
            return 'Custom setting: multiple quantities disabled';
        },
    },
}
</script>
